import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaStar, FaUsers } from 'react-icons/fa';
import { LuLayoutDashboard } from 'react-icons/lu';
import { useTheme } from '../context/ThemeContext';
import { useEmployee } from '../context/EmployeeContext';
import logo from '../Assets/HD Quality Logo 2.svg';

const EmployeeSidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const sidebarRef = useRef(null);
  const { sidebarCollapsed, setSidebarCollapsed } = useTheme();
  const { employee, loading } = useEmployee();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen, setSidebarOpen]);

  const isActive = (path) => location.pathname === path;

  const handleLinkClick = () => {
    if (window.innerWidth <= 1024) {
      setSidebarOpen(false);
    }
  };

  const calculateAverageRating = () => {
    if (!employee?.clients || employee.clients.length === 0) return '0.0';
    const totalRatings = employee.clients.reduce((sum, client) => sum + (client.feedback?.rating || 0), 0);
    return (totalRatings / employee.clients.length).toFixed(1);
  };

  const averageRating = calculateAverageRating();
  const employeeInitial = employee?.name?.[0]?.toUpperCase() || 'E';

  const NavLink = ({ to, icon: Icon, children, index = 0 }) => {
    const active = isActive(to);
    return (
      <Link
        to={to}
        onClick={handleLinkClick}
        className={`sidebar-link group ${active ? 'active' : ''} opacity-0 animate-fade-in-up`}
        style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
        title={sidebarCollapsed ? children : ''}
      >
        <Icon
          className={`w-5 h-5 transition-all duration-300 group-hover:scale-110 ${active ? 'text-secondary-400' : 'text-white/70 group-hover:text-secondary-400'}`}
        />
        {!sidebarCollapsed && <span className="font-medium">{children}</span>}
        {active && !sidebarCollapsed && (
          <div className="absolute right-3 w-2 h-2 bg-white rounded-full animate-pulse" />
        )}
      </Link>
    );
  };

  return (
    <aside
      ref={sidebarRef}
      className={`
        fixed top-0 left-0 z-50 h-screen
        bg-sidebar-gradient
        dark:from-black dark:via-neutral-950 dark:to-black
        transform transition-all duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarCollapsed ? 'lg:w-20 w-72' : 'w-72'}
        flex flex-col
        shadow-xl lg:shadow-none
      `}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/10 via-transparent to-primary-500/10 pointer-events-none" />

      <div className="flex items-center justify-between h-16 px-4 border-b border-white/10 gap-2 relative z-10">
        <div className={`flex-1 flex items-center ${sidebarCollapsed ? 'justify-center' : 'pr-2'}`}>
          {!sidebarCollapsed ? (
            <img src={logo} alt="BooksComply" className="h-10 w-auto max-w-[220px] object-contain" />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white font-bold text-sm">
              BC
            </div>
          )}
        </div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2 rounded-xl text-white/70 hover:bg-white/10 transition-colors"
        >
          <FaChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:block p-2 rounded-xl text-white/70 hover:bg-white/10 transition-colors"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <FaChevronRight className="w-4 h-4" />
          ) : (
            <FaChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className="px-4 py-5 border-b border-white/10 relative z-10">
        {loading ? (
          <div className="space-y-3">
            <div className="shimmer w-16 h-16 rounded-full mx-auto" />
            <div className="shimmer h-4 w-32 mx-auto rounded" />
          </div>
        ) : (
          <div className={`flex flex-col items-center text-center ${sidebarCollapsed ? 'px-0' : ''}`}>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-400 to-secondary-500 flex items-center justify-center overflow-hidden shadow-lg ring-2 ring-white/20 mb-3">
              {employee?.profileImage ? (
                <img src={employee.profileImage} alt={employee.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-white">{employeeInitial}</span>
              )}
            </div>
            {!sidebarCollapsed && (
              <>
                <h3 className="text-white font-semibold text-sm truncate max-w-full">{employee?.name || 'Employee'}</h3>
                <p className="text-white/50 text-xs mt-0.5">Employee Portal</p>
                <div className="flex items-center gap-1 mt-2">
                  {[...Array(5)].map((_, i) => (
                    <FaStar
                      key={i}
                      className={`w-3 h-3 ${i < Math.floor(Number(averageRating)) ? 'text-secondary-400' : 'text-white/20'}`}
                    />
                  ))}
                </div>
                <p className="text-white/60 text-xs mt-1 flex items-center gap-1">
                  <FaUsers className="w-3 h-3" />
                  {averageRating} · {employee?.clients?.length || 0} clients
                </p>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto pt-4 pb-4 px-3 scrollbar-hide relative z-10">
        <nav className="space-y-1">
          {!sidebarCollapsed && (
            <div className="px-4 mb-2">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-white/40">Navigation</h3>
              <div className="h-px bg-gradient-to-r from-primary-500/20 to-transparent mt-2" />
            </div>
          )}
          <NavLink to="/client" icon={LuLayoutDashboard} index={0}>
            Client Management
          </NavLink>
        </nav>
      </div>

      <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-3xl pointer-events-none opacity-30 bg-gradient-to-br from-primary-500 to-secondary-500" />
    </aside>
  );
};

export default EmployeeSidebar;
